/**
 * The staged library's rules (V7-w), kept out of the page.
 *
 * `arcade_path` (V7-b) already decides everything that matters — which
 * lessons are `locked`, how many `stars` each has, which single lesson is
 * `current` — and it recomputes all of it on every read. So nothing here
 * re-derives a lock or a star: these are the small readings the path view
 * needs on top of the answer (which state a row is in, how full a stage is,
 * whether a `/lesson/:id` link points at something the path still keeps
 * shut), written once and tested once instead of inline in JSX.
 */

/** The three star slots of the default thresholds; a panel change can make it more or fewer. */
export const DEFAULT_STAR_THRESHOLDS = [0, 75, 90]

/**
 * How one lesson row reads on the path, in the order the view cares about:
 *
 *   current   — the one lesson `arcade_path` points at; highlighted
 *   completed — done (Q12: every sentence has a scored attempt)
 *   locked    — dimmed, with the lock; tapping it explains rather than opens
 *   open      — unlocked, unfinished, but not the current one
 *
 * `completed` outranks `locked` because the backend never locks a done
 * lesson; it outranks nothing else because `current` is by definition the
 * first *unfinished* lesson, so the two cannot collide.
 */
export function lessonState(lesson) {
  if (lesson?.current) return 'current'
  if (lesson?.completed) return 'completed'
  if (lesson?.locked) return 'locked'
  return 'open'
}

/**
 * The same for a stage. `current` is the stage holding the current lesson —
 * `path.current.stageId`, not a field on the stage — so it is passed in.
 */
export function stageState(stage, current) {
  if (stage?.locked) return 'locked'
  if (current && stage?.id === current.stageId) return 'current'
  if (stage?.completed) return 'completed'
  return 'open'
}

/**
 * One boolean per star slot, so the row can draw the empty ones too: three
 * outlines from the first read, filling in as the score climbs. The slot
 * count follows `starThresholds` (an AppSetting) rather than a hard 3.
 */
export function starSlots(lesson, thresholds = DEFAULT_STAR_THRESHOLDS) {
  const total = Array.isArray(thresholds) && thresholds.length > 0 ? thresholds.length : DEFAULT_STAR_THRESHOLDS.length
  const earned = Math.max(0, Math.min(total, Math.round(lesson?.stars ?? 0)))
  return Array.from({ length: total }, (_, index) => index < earned)
}

/** How much of a stage is done, for its «۲ از ۵ درس» line and its bar. */
export function stageProgress(stage) {
  const lessons = stage?.lessons ?? []
  const done = lessons.filter((lesson) => lesson.completed).length
  return { done, total: lessons.length, percent: lessons.length === 0 ? 0 : Math.round((done / lessons.length) * 100) }
}

/** Every stage of the path, oldest-first as the server ordered them (never re-sorted here). */
export function pathStages(path) {
  return path?.stages ?? []
}

/** A path with no published stage at all — a level the panel has not laid out yet. */
export function isEmptyPath(path) {
  return pathStages(path).every((stage) => (stage.lessons ?? []).length === 0)
}

/** The lesson on the path with this id, wherever it sits, or null. */
export function findLesson(path, lessonId) {
  if (!lessonId) return null
  for (const stage of pathStages(path)) {
    const found = (stage.lessons ?? []).find((lesson) => lesson.id === lessonId)
    if (found) return found
  }
  return null
}

/**
 * Whether a direct `/lesson/:id` link points at a lesson the path still
 * keeps shut (Q13). It opens anyway — `lessons_detail` and `attempts_submit`
 * only refuse while `arcade.enforceLockOnSubmit` is on — so this decides a
 * banner, not a redirect. A lesson that is not on the path at all (no stage,
 * another level) is not locked: there is nothing to unlock it from.
 */
export function isLockedOnPath(path, lessonId) {
  return findLesson(path, lessonId)?.locked === true
}

/**
 * The 403 the premium gate answers with (Q22), read straight off the
 * `ApiErrorDto` shape rather than through `services/api`, so this module
 * stays free of the network layer — same arrangement as `classifyFailure`
 * in `lib/practiceSession`. `arcade.requirePremium` is off today, so this
 * is the path the app takes only once the client turns the gate on.
 */
export function isPremiumRequired(error) {
  return error?.response?.status === 403 && error?.response?.data?.error === 'premium_required'
}
